import { createActivityStore, createSessionStore } from "@/app/activity/store"
import { createGlobalStore } from "@/stores/global"
import BasicButton from "@/components/button/BasicButton"
import Dialog from "@/components/dialog/Dialog"
import DatePicker from "@/components/form/datePicker/DatePicker"
import Selector from "@/components/form/selector/Selector"
import TextField from "@/components/form/textField/TextField"
import TimePicker from "@/components/form/timePicker/TimePicker"
import {
  useCreateSessionMutation,
  useDeleteSessionMutation,
  useUpsertSessionMutation,
} from "@/hooks/api/useSessionQuery"
import { useSessionTypeQuery } from "@/hooks/api/useTeamQuery"
import { CreateSessionPayload, Session, SessionType, UpsertSessionPayload } from "@/types/session"
import { format, isBefore } from "date-fns"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { HOUR_TO_SECOND_FORMAT } from "@/constants/regex"
import "./upsertSessionDialog.scss"

export default function UpsertSessionDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()

  const { activity } = createActivityStore((state) => ({
    activity: state.activity,
  }))

  const { session, setSession, setUpsertDialogOpen, setTemporarySessionAction } =
    createSessionStore((state) => ({
      session: state.session,
      setSession: state.setSession,
      setTemporarySessionAction: state.setTemporarySession,
      setUpsertDialogOpen: state.setUpsertDialogOpen,
    }))

  const { data: sessionTypesRaw } = useSessionTypeQuery()
  const sessionTypes = useMemo(() => {
    if (!sessionTypesRaw) {
      return []
    }

    const uniqueMap = new Map()
    sessionTypesRaw.forEach((type) => {
      if (!uniqueMap.has(type.idfSessionType)) {
        uniqueMap.set(type.idfSessionType, type)
      }
    })

    return Array.from(uniqueMap.values())
  }, [sessionTypesRaw])

  const { setSnackbar } = createGlobalStore((state) => ({
    setSnackbar: state.setSnackBar,
  }))

  const { mutateAsync: createSession } = useCreateSessionMutation()
  const { mutateAsync: updateSession } = useUpsertSessionMutation()
  const { mutateAsync: deleteSession } = useDeleteSessionMutation(activity?.idfActivity)

  const [isDurationInValid, setIsDurationInValid] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [durationErrMessage, setDurationErrMessage] = useState("")

  const [temporarySession, setTemporarySession] = useState<Session | undefined>(session)

  const isValid = useMemo(() => {
    const { name, idfSessionType } = temporarySession || {}
    const isNameValid = !!name && name.length <= 35

    return !isDurationInValid && isNameValid && !!idfSessionType
  }, [temporarySession, isDurationInValid])

  useEffect(() => {
    setTemporarySession(session)
  }, [session])

  const upsertData = async () => {
    if (durationErrMessage) {
      setSnackbar({ message: durationErrMessage, severity: "warning", open: true })

      return
    }

    if (temporarySession?.idfSession) {
      if (
        isBefore(new Date(activity?.dateFinish as string), new Date(temporarySession.dateFinish)) ||
        isBefore(new Date(temporarySession.dateStart), new Date(activity?.dateStart as string))
      ) {
        setSnackbar({
          message: t("messages.notAppropriateSessionTimeDuration"),
          severity: "warning",
          open: true,
        })

        return
      }
      await updateSession({
        idfSession: temporarySession?.idfSession,
        payload: {
          ...temporarySession,
          dateStart: new Date(temporarySession.dateStart).toISOString(),
          dateFinish: new Date(temporarySession.dateFinish).toISOString(),
        } as unknown as UpsertSessionPayload,
      })

      setSnackbar({ message: t("messages.successUpdate"), severity: "success", open: true })
      setTemporarySession(undefined)
      setUpsertDialogOpen(false)
    } else {
      await createSession({
        ...temporarySession,
        dateStart: new Date(temporarySession?.dateStart as string).toISOString(),
        dateFinish: new Date(temporarySession?.dateFinish as string).toISOString(),
        idfActivity: activity?.idfActivity as number,
      } as CreateSessionPayload)

      setSnackbar({ message: t("messages.successAdd"), severity: "success", open: true })
      setTemporarySession(undefined)
      setUpsertDialogOpen(false)
    }
  }

  const deleteData = async () => {
    if (temporarySession?.idfSession) {
      try {
        await deleteSession(temporarySession?.idfSession)
        setIsDeleteDialogOpen(false)
        setSnackbar({ message: t("messages.successDelete"), severity: "success", open: true })
        setTemporarySession(undefined)
        setSession(undefined)
        setTemporarySessionAction(undefined)
        setUpsertDialogOpen(false)
      } catch (error) {
        console.error(error)
      }
    }
  }

  useEffect(() => {
    if (!temporarySession?.dateStart || !temporarySession?.dateFinish) {
      return
    }

    const isStartBeforeFinish = isBefore(
      new Date(temporarySession?.dateStart),
      new Date(temporarySession?.dateFinish),
    )

    const isBefore8Hour =
      new Date(temporarySession?.dateFinish).getTime() -
        new Date(temporarySession?.dateStart).getTime() <=
      8 * 60 * 60 * 1000

    // 백엔드(DateRange.ofDiffMore) 기준과 동일하게 정확히 1분(60초)은 허용한다
    const isAtLeast1Minute =
      new Date(temporarySession?.dateFinish).getTime() -
        new Date(temporarySession?.dateStart).getTime() >=
      60 * 1000

    const isBeforeActivityStart = isBefore(
      new Date(temporarySession?.dateStart),
      new Date(activity?.dateStart as string),
    )
    const isAfterActivityFinish = isBefore(
      new Date(activity?.dateFinish as string),
      new Date(temporarySession?.dateFinish),
    )

    setDurationErrMessage(t(""))

    if (!isAtLeast1Minute) {
      // Localazy에 번역값이 비어 있는 동안에도 문구가 보이도록 폴백 처리
      setDurationErrMessage(
        t("messages.minimumActivityDuration") || "Activity duration must be at least 1 minute.",
      )
    }

    if (isBeforeActivityStart || isAfterActivityFinish) {
      setDurationErrMessage(t("messages.notAppropriateSessionTimeDuration"))
    }

    if (!isStartBeforeFinish) {
      setDurationErrMessage(t("rules.failTimeOrder"))
    }

    if (!isBefore8Hour) {
      setDurationErrMessage(t("messages.maximumActivityDuration"))
    }

    if (
      isStartBeforeFinish &&
      isBefore8Hour &&
      isAtLeast1Minute &&
      !isBeforeActivityStart &&
      !isAfterActivityFinish
    ) {
      setDurationErrMessage("")
    }

    setIsDurationInValid(!isStartBeforeFinish || !isBefore8Hour || !isAtLeast1Minute)
  }, [temporarySession?.dateFinish, temporarySession?.dateStart, activity, temporarySession])

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={t(temporarySession?.idfSession ? "editSession" : "addSession")}
        maxWidth="900px"
        className="upsert-session-dialog"
        footer={
          <div className="upsert-session-dialog-footer">
            <div>
              <BasicButton size="sm" type="default-outlined" onClick={onClose}>
                {t("button.cancel")}
              </BasicButton>
              <BasicButton
                disabled={!isValid}
                size="sm"
                type="primary-contained"
                onClick={upsertData}>
                {t("button.save")}
              </BasicButton>
            </div>
            {temporarySession?.idfSession && (
              <BasicButton
                size="sm"
                type="danger-outlined"
                onClick={() => setIsDeleteDialogOpen(true)}>
                {t("button.delete")}
              </BasicButton>
            )}
          </div>
        }>
        <div className="upsert-session-dialog-content">
          <Selector
            options={sessionTypes || []}
            label={t("type")}
            placeholder={t("messages.selectSessionType")}
            value={
              sessionTypes?.find(
                (type) => type.idfSessionType === Number(temporarySession?.idfSessionType),
              ) || null
            }
            labelKey="name"
            getOptionKey={(option: SessionType) => option.idfSessionType}
            freeSolo={false}
            autoComplete
            autoHighlight
            isOptionEqualToValue={(option: SessionType, value: SessionType) => {
              return option.idfSessionType === value.idfSessionType
            }}
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase().trim()
              if (!inputValue) return options

              return options.filter((option) => {
                const optionName = option.name.toLowerCase()
                return optionName.includes(inputValue)
              })
            }}
            slotProps={{
              popper: {
                style: { zIndex: 1302 },
              },
            }}
            onChange={(_: unknown, value: SessionType) => {
              setTemporarySession({
                ...temporarySession,
                idfSessionType: value?.idfSessionType,
              } as Session)
            }}
          />
          <TextField
            label={t("name")}
            placeholder={t("messages.enterSessionTitle")}
            defaultValue={temporarySession?.name}
            onChange={(e) => {
              const { value } = e.target

              setTemporarySession({ ...temporarySession, name: value } as Session)
            }}
            error={(temporarySession?.name?.length ?? 0) > 35}
            showMessage={(temporarySession?.name?.length ?? 0) > 35}
            message={t("rules.max_len", { name: t("name"), max_len: 35 })}
          />
          <div className="date-time-selector-wrapper">
            <div className="date-time-selector-header">
              <label className="date-time-selector-label">{t("duration")}</label>
            </div>
            <div className="date-time-selector-body">
              <div className="date-time-selector">
                <DatePicker
                  slotProps={{
                    textField: {
                      error: isDurationInValid,
                      fullWidth: true,
                    },
                    popper: {
                      style: { zIndex: 1302 },
                    },
                  }}
                  value={new Date(temporarySession?.dateStart as string)}
                  onChange={(value) => {
                    if (value) {
                      const [hour, minute, second] = format(
                        temporarySession?.dateStart as string,
                        HOUR_TO_SECOND_FORMAT,
                      )
                        .split(":")
                        .map(Number)

                      setTemporarySession({
                        ...temporarySession,
                        dateStart: new Date(value.setHours(hour, minute, second)).toString(),
                      } as Session)
                    }
                  }}
                />
                <TimePicker
                  slotProps={{
                    textField: {
                      error: isDurationInValid,
                      fullWidth: true,
                    },
                    popper: {
                      style: { zIndex: 1302 },
                    },
                  }}
                  value={new Date(temporarySession?.dateStart as string)}
                  onChange={(value) => {
                    setTemporarySession({
                      ...temporarySession,
                      dateStart: value?.toString(),
                    } as Session)
                  }}
                />
              </div>
              <p>~</p>
              <div className="date-time-selector">
                <DatePicker
                  slotProps={{
                    textField: {
                      error: isDurationInValid,
                      fullWidth: true,
                    },
                    popper: {
                      style: { zIndex: 1302 },
                    },
                  }}
                  value={new Date(temporarySession?.dateFinish as string)}
                  onChange={(value) => {
                    if (value) {
                      const [hour, minute, second] = format(
                        temporarySession?.dateFinish as string,
                        HOUR_TO_SECOND_FORMAT,
                      )
                        .split(":")
                        .map(Number)

                      setTemporarySession({
                        ...temporarySession,
                        dateFinish: new Date(value.setHours(hour, minute, second)).toString(),
                      } as Session)
                    }
                  }}
                />
                <TimePicker
                  slotProps={{
                    textField: {
                      error: isDurationInValid,
                      fullWidth: true,
                    },
                    popper: {
                      style: { zIndex: 1302 },
                    },
                  }}
                  value={new Date(temporarySession?.dateFinish as string)}
                  onChange={(value) => {
                    setTemporarySession({
                      ...temporarySession,
                      dateFinish: value?.toString(),
                    } as Session)
                  }}
                />
              </div>
            </div>
            {isDurationInValid && durationErrMessage && (
              <p className="duration-error-message">{durationErrMessage}</p>
            )}
          </div>
        </div>
      </Dialog>
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title={t("confirm")}
        maxWidth="400px"
        className="delete-session-dialog"
        footer={
          <div className="delete-session-dialog-footer">
            <BasicButton
              size="sm"
              type="default-outlined"
              onClick={() => setIsDeleteDialogOpen(false)}>
              {t("button.cancel")}
            </BasicButton>
            <BasicButton size="sm" type="danger-outlined" onClick={deleteData}>
              {t("button.delete")}
            </BasicButton>
          </div>
        }>
        <div className="delete-session-dialog-content">
          <p>{t("messages.confirmDelete")}</p>
        </div>
      </Dialog>
    </>
  )
}
